import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../../services/api';
import GalleryGridEditor from '../GalleryGridEditor';
import useMediaFiltering from '../../hooks/useMediaFiltering';
import useCreditsTab from '../../hooks/useCreditsTab';
import useGalleryConfig from '../../hooks/useGalleryConfig';
import VisibilityToggle from './VisibilityToggle';
import './CleanTemplate.css';

function CleanTemplate({
  profileData,
  basicUser,
  isOwnProfile,
  onEdit,
  onTextClick,
  onColorClick,
  hiddenSections = [],
  onToggleSection,
  onPhotoClick,
  galleryEditMode,
  onToggleGalleryEdit,
  onGalleryConfigChange,
  onGalleryReorder,
  galleryConfig,
  onGalleryPhotoCrop,
}) {
  const [activePage, setActivePage] = useState('home');

  const { settings, performer, credits, skills, media, links } = profileData;

  const accentColor = settings?.accent_color || '#000000';
  const headingFont = settings?.heading_font || 'Cormorant Garamond';
  const bodyFont = settings?.body_font || 'Cormorant Garamond';
  const headingFontSize = settings?.heading_font_size || '32';
  const bodyFontSize = settings?.body_font_size || '16';

  // Profile display data
  const displayName =
    performer?.display_name || basicUser?.full_name || basicUser?.username || 'Unknown';
  const tagline = performer?.tagline || performer?.job_title || basicUser?.industry || '';
  const bio = performer?.bio || basicUser?.bio || '';

  // Shared hooks
  const { headshots, aboutPhotos, galleryPhotos, videos } = useMediaFiltering(media);
  const { galleryGridStyle } = useGalleryConfig(settings);
  const { activeCreditsTab, setActiveCreditsTab, creditCategories } = useCreditsTab(credits);

  // Fallback: use headshots if no specific slot photos, then all images for backward compat
  const heroImages =
    headshots.length > 0 ? headshots : media ? media.filter((m) => m.media_type === 'image') : [];

  return (
    <div
      className="clean-template"
      style={{
        '--accent-color': accentColor,
        '--heading-font': `'${headingFont}', Georgia, serif`,
        '--body-font': `'${bodyFont}', Georgia, serif`,
        '--heading-font-size': `${headingFontSize}px`,
        '--body-font-size': `${bodyFontSize}px`,
      }}
    >
      {/* Top Black Bar with Name */}
      <header className="clean-header">
        <div className="clean-header-inner">
          <div className="clean-header-left">
            <Link to="/feed" className="clean-back-btn" aria-label="Back to feed">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </Link>
            <nav className="clean-header-nav">
              <button
                className={activePage === 'home' ? 'active' : ''}
                onClick={() => setActivePage('home')}
              >
                home
              </button>
              {(!hiddenSections.includes('about') || onToggleSection) && (
                <button
                  className={`${activePage === 'about' ? 'active' : ''}${hiddenSections.includes('about') ? ' nav-link-hidden' : ''}`}
                  onClick={() => setActivePage('about')}
                >
                  about
                </button>
              )}
              {(!hiddenSections.includes('media') || onToggleSection) &&
              settings?.media_mode === 'separate' ? (
                <>
                  <button
                    className={`${activePage === 'videos' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                    onClick={() => setActivePage('videos')}
                  >
                    videos
                  </button>
                  <button
                    className={`${activePage === 'gallery' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                    onClick={() => setActivePage('gallery')}
                  >
                    gallery
                  </button>
                </>
              ) : (!hiddenSections.includes('media') || onToggleSection) &&
                settings?.media_mode === 'gallery_only' ? (
                <button
                  className={`${activePage === 'gallery' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                  onClick={() => setActivePage('gallery')}
                >
                  gallery
                </button>
              ) : (!hiddenSections.includes('media') || onToggleSection) &&
                settings?.media_mode === 'videos_only' ? (
                <button
                  className={`${activePage === 'videos' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                  onClick={() => setActivePage('videos')}
                >
                  videos
                </button>
              ) : !hiddenSections.includes('media') || onToggleSection ? (
                <button
                  className={`${activePage === 'media' ? 'active' : ''}${hiddenSections.includes('media') ? ' nav-link-hidden' : ''}`}
                  onClick={() => setActivePage('media')}
                >
                  media
                </button>
              ) : null}
              {creditCategories.length > 0 &&
                (!hiddenSections.includes('resume') || onToggleSection) && (
                  <button
                    className={`${activePage === 'resume' ? 'active' : ''}${hiddenSections.includes('resume') ? ' nav-link-hidden' : ''}`}
                    onClick={() => setActivePage('resume')}
                  >
                    resume
                  </button>
                )}
              {(!hiddenSections.includes('contact') || onToggleSection) && (
                <button
                  className={`${activePage === 'contact' ? 'active' : ''}${hiddenSections.includes('contact') ? ' nav-link-hidden' : ''}`}
                  onClick={() => setActivePage('contact')}
                >
                  contact
                </button>
              )}
            </nav>
          </div>

          <h1
            className={`clean-header-name${onTextClick ? ' showcase-editable' : ''}`}
            onClick={
              onTextClick
                ? (e) => {
                    e.stopPropagation();
                    onTextClick('heading', e);
                  }
                : undefined
            }
          >
            {displayName}
          </h1>

          <div className="clean-header-right">
            {links && links.length > 0 && (
              <div className="clean-header-social">
                {links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.label || link.link_type}
                  >
                    {link.link_type === 'instagram' && (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    )}
                    {link.link_type === 'youtube' && (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    )}
                    {link.link_type === 'twitter' && (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                    )}
                    {link.link_type === 'vimeo' && (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z" />
                      </svg>
                    )}
                    {link.link_type === 'website' && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                    )}
                    {!['instagram', 'twitter', 'youtube', 'vimeo', 'website'].includes(
                      link.link_type
                    ) && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    )}
                  </a>
                ))}
              </div>
            )}
            {isOwnProfile && (
              <button className="clean-edit-btn" onClick={onEdit}>
                Edit
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Tagline - shown on all pages */}
      {tagline && (
        <div className="clean-tagline">
          <span>{tagline}</span>
        </div>
      )}

      {/* HOME PAGE - Hero Photos */}
      {activePage === 'home' && (
        <section className="clean-hero-photos">
          {heroImages.length >= 4 ? (
            <div className="clean-hero-grid clean-hero-grid-four">
              {heroImages.slice(0, 4).map((img, i) => (
                <div key={img.id || i} className="clean-hero-img">
                  <img
                    src={getMediaUrl(img.url)}
                    alt={img.title || displayName}
                    style={{
                      objectPosition: `${img.position_x ?? 50}% ${img.position_y ?? 50}%`,
                      transform:
                        (img.zoom ?? 100) > 100 ? `scale(${(img.zoom ?? 100) / 100})` : undefined,
                      transformOrigin: `${img.position_x ?? 50}% ${img.position_y ?? 50}%`,
                    }}
                  />
                  {isOwnProfile && onPhotoClick && (
                    <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      <span>Change</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : heroImages.length === 3 ? (
            <div className="clean-hero-grid clean-hero-grid-three">
              {heroImages.slice(0, 3).map((img, i) => (
                <div key={img.id || i} className="clean-hero-img">
                  <img
                    src={getMediaUrl(img.url)}
                    alt={img.title || displayName}
                    style={{
                      objectPosition: `${img.position_x ?? 50}% ${img.position_y ?? 50}%`,
                      transform:
                        (img.zoom ?? 100) > 100 ? `scale(${(img.zoom ?? 100) / 100})` : undefined,
                      transformOrigin: `${img.position_x ?? 50}% ${img.position_y ?? 50}%`,
                    }}
                  />
                  {isOwnProfile && onPhotoClick && (
                    <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      <span>Change</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : heroImages.length === 2 ? (
            <div className="clean-hero-grid">
              <div className="clean-hero-img">
                <img
                  src={getMediaUrl(heroImages[0].url)}
                  alt={heroImages[0].title || displayName}
                  style={{
                    objectPosition: `${heroImages[0].position_x ?? 50}% ${heroImages[0].position_y ?? 50}%`,
                    transform:
                      (heroImages[0].zoom ?? 100) > 100
                        ? `scale(${(heroImages[0].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${heroImages[0].position_x ?? 50}% ${heroImages[0].position_y ?? 50}%`,
                  }}
                />
                {isOwnProfile && onPhotoClick && (
                  <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Change</span>
                  </div>
                )}
              </div>
              <div className="clean-hero-img">
                <img
                  src={getMediaUrl(heroImages[1].url)}
                  alt={heroImages[1].title || displayName}
                  style={{
                    objectPosition: `${heroImages[1].position_x ?? 50}% ${heroImages[1].position_y ?? 50}%`,
                    transform:
                      (heroImages[1].zoom ?? 100) > 100
                        ? `scale(${(heroImages[1].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${heroImages[1].position_x ?? 50}% ${heroImages[1].position_y ?? 50}%`,
                  }}
                />
                {isOwnProfile && onPhotoClick && (
                  <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Change</span>
                  </div>
                )}
              </div>
            </div>
          ) : heroImages.length === 1 ? (
            <div className="clean-hero-grid clean-hero-grid-single">
              <div className="clean-hero-img">
                <img
                  src={getMediaUrl(heroImages[0].url)}
                  alt={heroImages[0].title || displayName}
                  style={{
                    objectPosition: `${heroImages[0].position_x ?? 50}% ${heroImages[0].position_y ?? 50}%`,
                    transform:
                      (heroImages[0].zoom ?? 100) > 100
                        ? `scale(${(heroImages[0].zoom ?? 100) / 100})`
                        : undefined,
                    transformOrigin: `${heroImages[0].position_x ?? 50}% ${heroImages[0].position_y ?? 50}%`,
                  }}
                />
                {isOwnProfile && onPhotoClick && (
                  <div className="photo-edit-overlay" onClick={() => onPhotoClick('headshot')}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span>Change</span>
                  </div>
                )}
              </div>
            </div>
          ) : basicUser?.profile_image ? (
            <div className="clean-hero-grid clean-hero-grid-single">
              <div className="clean-hero-img">
                <img src={basicUser.profile_image} alt={displayName} />
              </div>
            </div>
          ) : (
            <div className="clean-hero-grid clean-hero-grid-single">
              <div className="clean-hero-img clean-hero-img-placeholder">
                <span>{displayName.charAt(0).toUpperCase()}</span>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ABOUT PAGE */}
      {activePage === 'about' && (!hiddenSections.includes('about') || onToggleSection) && (
        <section className="clean-about">
          <div className="clean-about-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2
                className={`clean-section-title${onTextClick ? ' showcase-editable' : ''}`}
                onClick={
                  onTextClick
                    ? (e) => {
                        e.stopPropagation();
                        onTextClick('heading', e);
                      }
                    : undefined
                }
                style={{ marginBottom: 0 }}
              >
                About
              </h2>
              <VisibilityToggle
                section="about"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            <div className="clean-about-content">
              <div className="clean-about-photo">
                {aboutPhotos.length > 0 ? (
                  <>
                    <img
                      src={getMediaUrl(aboutPhotos[0].url)}
                      alt={displayName}
                      style={{
                        objectPosition: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                        transform:
                          (aboutPhotos[0].zoom ?? 100) > 100
                            ? `scale(${(aboutPhotos[0].zoom ?? 100) / 100})`
                            : undefined,
                        transformOrigin: `${aboutPhotos[0].position_x ?? 50}% ${aboutPhotos[0].position_y ?? 50}%`,
                      }}
                    />
                    {isOwnProfile && onPhotoClick && (
                      <div className="photo-edit-overlay" onClick={() => onPhotoClick('about')}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                        <span>Change</span>
                      </div>
                    )}
                  </>
                ) : basicUser?.profile_image ? (
                  <img src={basicUser.profile_image} alt={displayName} />
                ) : (
                  <div className="clean-about-placeholder">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div
                className={`clean-about-text${onTextClick ? ' showcase-editable' : ''}`}
                onClick={
                  onTextClick
                    ? (e) => {
                        e.stopPropagation();
                        onTextClick('body', e);
                      }
                    : undefined
                }
              >
                {bio ? (
                  bio.split('\n\n').map((paragraph, index) => <p key={index}>{paragraph}</p>)
                ) : (
                  <p>Add your bio in the profile settings to tell your story.</p>
                )}
                {performer &&
                  (performer.union_status || performer.representation || performer.height) && (
                    <div className="clean-about-stats">
                      {performer.union_status && <span>{performer.union_status}</span>}
                      {performer.height && <span>{performer.height}</span>}
                      {performer.age_range && <span>Age Range: {performer.age_range}</span>}
                      {performer.representation && <span>Rep: {performer.representation}</span>}
                    </div>
                  )}
                {skills && skills.length > 0 && (
                  <div className="clean-skills">
                    <h4 className="clean-skills-label">Skills</h4>
                    <div className="clean-skills-list">
                      {skills.map((skill, index) => (
                        <span key={index} className="clean-skill-tag">
                          {skill.name || skill.skill || skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MEDIA PAGE - Together mode: videos + gallery on one page */}
      {activePage === 'media' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="clean-media">
          <div className="clean-media-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="clean-section-title" style={{ marginBottom: 0 }}>
                Media
              </h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>

            {videos.length > 0 && (
              <div className="clean-videos">
                {videos.map((item, index) => (
                  <div key={index} className="clean-video-item">
                    {item.embed_url ? (
                      <div className="clean-video-wrap">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="clean-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="clean-video-title">{item.title}</p>}
                  </div>
                ))}
              </div>
            )}

            {galleryPhotos.length > 0 && (
              <div style={{ position: 'relative' }}>
                {isOwnProfile && onToggleGalleryEdit && (
                  <button
                    className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                    onClick={onToggleGalleryEdit}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                    </svg>
                    {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
                  </button>
                )}
                {galleryEditMode && onGalleryConfigChange ? (
                  <GalleryGridEditor
                    photos={galleryPhotos}
                    config={galleryConfig}
                    onChange={onGalleryConfigChange}
                    onReorder={onGalleryReorder}
                    onCropPhoto={onGalleryPhotoCrop}
                    theme="clean"
                  />
                ) : (
                  <div className="clean-gallery-grid" style={galleryGridStyle}>
                    {galleryPhotos.map((item) => (
                      <div key={item.id} className="clean-gallery-item">
                        <img
                          src={getMediaUrl(item.url)}
                          alt={item.title || 'Gallery image'}
                          style={{
                            objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                            transform:
                              (item.zoom ?? 100) > 100
                                ? `scale(${(item.zoom ?? 100) / 100})`
                                : undefined,
                            transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                          }}
                        />
                        {isOwnProfile && onGalleryPhotoCrop && (
                          <div
                            className="gallery-photo-crop-overlay"
                            onClick={(e) => onGalleryPhotoCrop(item, e)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                              <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                            </svg>
                            <span>Crop</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {videos.length === 0 && galleryPhotos.length === 0 && (
              <p className="clean-empty-state">
                No media added yet. Add photos and videos in profile settings.
              </p>
            )}
          </div>
        </section>
      )}

      {/* VIDEOS PAGE - Separate mode */}
      {activePage === 'videos' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="clean-media">
          <div className="clean-media-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="clean-section-title" style={{ marginBottom: 0 }}>
                Videos
              </h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {videos.length > 0 ? (
              <div className="clean-videos">
                {videos.map((item, index) => (
                  <div key={index} className="clean-video-item">
                    {item.embed_url ? (
                      <div className="clean-video-wrap">
                        <iframe
                          src={item.embed_url}
                          title={item.title || 'Video'}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="clean-video-placeholder">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                    {item.title && <p className="clean-video-title">{item.title}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="clean-empty-state">No videos added yet.</p>
            )}
          </div>
        </section>
      )}

      {/* GALLERY PAGE - Separate mode */}
      {activePage === 'gallery' && (!hiddenSections.includes('media') || onToggleSection) && (
        <section className="clean-media">
          <div className="clean-media-inner" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="clean-section-title" style={{ marginBottom: 0 }}>
                Gallery
              </h2>
              <VisibilityToggle
                section="media"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {isOwnProfile && onToggleGalleryEdit && (
              <button
                className={`gallery-edit-btn${galleryEditMode ? ' active' : ''}`}
                onClick={onToggleGalleryEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                {galleryEditMode ? 'Done Editing' : 'Edit Gallery Layout'}
              </button>
            )}
            {galleryEditMode && onGalleryConfigChange ? (
              <GalleryGridEditor
                photos={galleryPhotos}
                config={galleryConfig}
                onChange={onGalleryConfigChange}
                onReorder={onGalleryReorder}
                onCropPhoto={onGalleryPhotoCrop}
                theme="clean"
              />
            ) : galleryPhotos.length > 0 ? (
              <div className="clean-gallery-grid" style={galleryGridStyle}>
                {galleryPhotos.map((item) => (
                  <div key={item.id} className="clean-gallery-item">
                    <img
                      src={getMediaUrl(item.url)}
                      alt={item.title || 'Gallery image'}
                      style={{
                        objectPosition: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                        transform:
                          (item.zoom ?? 100) > 100
                            ? `scale(${(item.zoom ?? 100) / 100})`
                            : undefined,
                        transformOrigin: `${item.position_x ?? 50}% ${item.position_y ?? 50}%`,
                      }}
                    />
                    {isOwnProfile && onGalleryPhotoCrop && (
                      <div
                        className="gallery-photo-crop-overlay"
                        onClick={(e) => onGalleryPhotoCrop(item, e)}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                          <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                        </svg>
                        <span>Crop</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="clean-empty-state">
                No photos added yet. Add them in profile settings.
              </p>
            )}
          </div>
        </section>
      )}

      {/* RESUME PAGE */}
      {activePage === 'resume' && (!hiddenSections.includes('resume') || onToggleSection) && (
        <section className="clean-resume">
          <div className="clean-resume-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="clean-section-title" style={{ marginBottom: 0 }}>
                Resume
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {performer?.resume_pdf_url && (
                  <a
                    href={getMediaUrl(performer.resume_pdf_url)}
                    download
                    className="clean-resume-download"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download PDF
                  </a>
                )}
                <VisibilityToggle
                  section="resume"
                  hiddenSections={hiddenSections}
                  onToggleSection={onToggleSection}
                />
              </div>
            </div>

            {creditCategories.length > 0 ? (
              <>
                <div className="clean-credits-tabs">
                  {creditCategories.map((category) => (
                    <button
                      key={category}
                      className={`clean-credits-tab ${activeCreditsTab === category ? 'active' : ''}`}
                      onClick={() => setActiveCreditsTab(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {activeCreditsTab && credits[activeCreditsTab] && (
                  <table className="clean-credits-table">
                    <thead>
                      <tr>
                        <th>Production</th>
                        <th>Role</th>
                        <th>Company</th>
                        <th>Director</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits[activeCreditsTab].map((credit, index) => (
                        <tr key={index}>
                          <td>
                            <Link
                              to={`/production?name=${encodeURIComponent(credit.production)}${credit.company ? `&company=${encodeURIComponent(credit.company)}` : ''}`}
                              className="credit-production-link"
                            >
                              {credit.production}
                            </Link>
                          </td>
                          <td>{credit.role}</td>
                          <td>{credit.company || ''}</td>
                          <td>{credit.director || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : (
              <p className="clean-empty-state">No credits added yet.</p>
            )}

            {performer?.resume_url && (
              <a href={performer.resume_url} download className="clean-download-btn">
                Download Resume PDF
              </a>
            )}
          </div>
        </section>
      )}

      {/* CONTACT PAGE */}
      {activePage === 'contact' && (!hiddenSections.includes('contact') || onToggleSection) && (
        <section className="clean-contact">
          <div className="clean-contact-inner">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="clean-section-title" style={{ marginBottom: 0 }}>
                Contact
              </h2>
              <VisibilityToggle
                section="contact"
                hiddenSections={hiddenSections}
                onToggleSection={onToggleSection}
              />
            </div>
            {performer?.contact_blurb && (
              <p className="clean-contact-blurb">{performer.contact_blurb}</p>
            )}
            <div className="clean-contact-content">
              {performer?.representation && (
                <div className="clean-contact-item">
                  <span className="clean-contact-label">Representation</span>
                  <span className="clean-contact-value">{performer.representation}</span>
                </div>
              )}
              {basicUser?.location && (
                <div className="clean-contact-item">
                  <span className="clean-contact-label">Location</span>
                  <span className="clean-contact-value">{basicUser.location}</span>
                </div>
              )}
              {links &&
                links
                  .filter((l) => l.is_public !== 0)
                  .map((link, index) => (
                    <div key={index} className="clean-contact-item">
                      <span className="clean-contact-label">{link.label}</span>
                      <span className="clean-contact-value">
                        {link.url.startsWith('http') ? (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="clean-contact-link"
                          >
                            {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        ) : (
                          link.url
                        )}
                      </span>
                    </div>
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="clean-footer">
        <span>
          &copy; {new Date().getFullYear()} {displayName}
        </span>
      </footer>
    </div>
  );
}

export default CleanTemplate;
